import { useState } from 'react';
import {
  IState,
  IModel,
  IStateInstitution,
  minStep,
  maxStep,
} from './types.form';
import { GroupDto } from 'common/dtos/group.dto';
import { ProfileDto } from 'common/dtos/profile.dto';
import { StatusDto } from 'common/dtos/status.dto';
import { ErrorDto } from 'common/dtos/error.dto';
import { InstitutionStatusEnum } from 'common/enums';

// Helper: compute lists when adding a status
export const computeToAddOnAdd = (
  state: IState,
  statusDescription: InstitutionStatusEnum,
  initialSet = false,
) => {
  const toDelStatusList = state.toDelStatusList.filter(
    (item) => item.name !== statusDescription,
  );

  const wasAdded = state.toAddStatusList.some(
    (item) => item.name === statusDescription,
  );
  const hasStatus = state.institution.statusList?.some(
    (item) => item.name === statusDescription,
  );

  let toAddStatusList = state.toAddStatusList;
  if ((!wasAdded && !hasStatus) || initialSet) {
    const newStatus = state.statusList.find((item) => item.name === statusDescription);
    if (newStatus) toAddStatusList = [...state.toAddStatusList, newStatus];
  }

  return { toAddStatusList, toDelStatusList };
};

// Helper: compute lists when deleting a status
export const computeToDelOnDel = (
  state: IState,
  statusDescription: InstitutionStatusEnum,
) => {
  const toAddStatusList = state.toAddStatusList.filter(
    (item) => item.name !== statusDescription,
  );

  const wasAdded = state.toDelStatusList.some(
    (item) => item.name === statusDescription,
  );
  const hasStatus = state.institution.statusList?.some(
    (item) => item.name === statusDescription,
  );

  let toDelStatusList = state.toDelStatusList;
  if (!wasAdded && hasStatus) {
    const newStatus = state.statusList.find((item) => item.name === statusDescription);
    if (newStatus) toDelStatusList = [...state.toDelStatusList, newStatus];
  }

  return { toAddStatusList, toDelStatusList };
};

// Helper: compute new state for formik field change (pure)
export const computeStateOnFormikChange = (prevState: IState, field: string, value: any): IState => {
  const newState = { ...prevState } as any;
  const keys = field.split(/[.[\]]/).filter(Boolean);

  keys.reduce((acc: any, key: any, index: number) => {
    const nextKey = isNaN(key) ? key : Number(key);

    if (index === keys.length - 1) {
      acc[nextKey] = value;
    } else {
      if (acc[nextKey] === undefined) {
        acc[nextKey] = typeof keys[index + 1] === 'number' ? [] : {};
      }
      return acc[nextKey];
    }

    return acc;
  }, newState);

  return newState as IState;
};

const useModel = (): IModel => {
  const [state, setState] = useState<IState>({
    institution: {
      uuid: '',
      name: '',
      fantasyName: '',
      identification: '',
      caf: '',
      email: '',
      address: {
        uuid: '',
        zipcode: '',
        municipality: '',
        district: '',
        street: '',
        number: '',
        complement: '',
      },
      group: {
        uuid: '',
        name: '',
        description: '',
      },
      profile: {
        uuid: '',
        name: '',
        description: '',
      },
      contactList: [],
      statusList: [],
    },
    groupList: [
      {
        uuid: '',
        name: '',
        description: '',
      },
    ],
    profileList: [
      {
        uuid: '',
        name: '',
        description: '',
      },
    ],
    statusList: [],
    toAddStatusList: [],
    toDelStatusList: [],
    error: null,
    step: 1,
    hasError: false,
    hasFinishInstitutionUpdate: false,
  });

  const [screenError, setScreenError] = useState<string>('');

  const setInstitution = (institution: IStateInstitution) => {
    setState((prevState) => ({ ...prevState, institution }));
  };

  const setGroupList = (groupList: GroupDto[]) => {
    setState((prevState) => ({ ...prevState, groupList }));
  };

  const setProfileList = (profileList: ProfileDto[]) => {
    setState((prevState) => ({ ...prevState, profileList }));
  };

  const setStatusList = (statusList: StatusDto[]) => {
    setState((prevState) => ({ ...prevState, statusList }));
  };

  const setToAddStatusList = (
    statusDescription: InstitutionStatusEnum,
    initialSet = false,
  ) => {
    // Remove do vetor de status que seram removidos, o status que será adicionado.
    const toDelStatusList = state.toDelStatusList.filter(
      (item) => item.name !== statusDescription,
    );
    setState((prevState) => ({ ...prevState, toDelStatusList }));

    // Adiciona o status no vetor de status a serem aplicados, caso o status não esteja aplicado e não esteja na lista que será aplicada.
    const wasAdded = state.toAddStatusList.some(
      (item) => item.name === statusDescription,
    );
    const hasStatus = state.institution.statusList?.some(
      (item) => item.name === statusDescription,
    );

    if ((!wasAdded && !hasStatus) || initialSet) {
      const newStatus = state.statusList.find(
        (item) => item.name === statusDescription,
      );
      if (newStatus)
        setState((prevState) => ({
          ...prevState,
          toAddStatusList: [...state.toAddStatusList, newStatus],
        }));
    }
  };

  const setToDelStatusList = (statusDescription: InstitutionStatusEnum) => {
    // Remove do vetor de status que seram aplicados, o status que será deletedo.
    const toAddStatusList = state.toAddStatusList.filter(
      (item) => item.name !== statusDescription,
    );
    setState((prevState) => ({ ...prevState, toAddStatusList }));

    // Adiciona o status no vetor de status a serem deletados, caso o status esteja aplicado e nao lista que será deletada.
    const wasAdded = state.toDelStatusList.some(
      (item) => item.name === statusDescription,
    );
    const hasStatus = state.institution.statusList?.some(
      (item) => item.name === statusDescription,
    );
    if (!wasAdded && hasStatus) {
      const newStatus = state.statusList.find(
        (item) => item.name === statusDescription,
      );
      if (newStatus)
        setState((prevState) => ({
          ...prevState,
          toDelStatusList: [...state.toDelStatusList, newStatus],
        }));
    }
  };

  const setError = (error: ErrorDto | null) => {
    setState((prevState) => ({ ...prevState, error }));
  };

  const setIncreaseStep = () => {
    if (state.step === 1) {
      const contact = state.institution.contactList[0];
      if (contact.value === '') {
        setScreenError('Obrigatório informar o telefone da instituição');
        return;
      }
    }
    const newStep = state.step + 1;
    if (newStep <= maxStep)
      setState((prevState) => ({ ...prevState, step: newStep }));
  };

  const setDecreaseStep = () => {
    const newStep = state.step - 1;
    if (newStep >= minStep)
      setState((prevState) => ({ ...prevState, step: newStep }));
  };

  const setHasError = (hasError: boolean) => {
    setState((prevState) => ({ ...prevState, hasError }));
  };

  const setHasFinishInstitutionUpdate = (
    hasFinishInstitutionUpdate: boolean,
  ) => {
    setState((prevState) => ({ ...prevState, hasFinishInstitutionUpdate }));
  };

  const setStateOnFormikChange = (field: string, value: any) => {
    setState((prevState) => {
      const newState = { ...prevState }; // Cria uma cópia do estado para evitar mutações

      const keys = field.split(/[.[\]]/).filter(Boolean);

      // Armazena o retorno da reduce, mesmo que não use diretamente
      const _ = keys.reduce((acc: any, key: any, index) => {
        const nextKey = isNaN(key) ? key : Number(key);

        if (index === keys.length - 1) {
          acc[nextKey] = value;
        } else {
          if (acc[nextKey] === undefined) {
            acc[nextKey] = typeof keys[index + 1] === 'number' ? [] : {};
          }

          return acc[nextKey];
        }

        return acc;
      }, newState);

      return newState;
    });
  };


  return {
    state,
    screenError,
    setInstitution,
    setToAddStatusList,
    setToDelStatusList,
    setGroupList,
    setProfileList,
    setStatusList,
    setError,
    setIncreaseStep,
    setDecreaseStep,
    setHasError,
    setHasFinishInstitutionUpdate,
    setStateOnFormikChange,
    setScreenError,
  };
};
export default useModel;
